import { useMemo, useState } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useTakeoff } from "./store";
import { money } from "./pricing";
import { fmtDuration } from "./timeTracker";
import { CARD, MONO, StatusBadge } from "./theme";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
  { days: 30, label: "30 days" },
  { days: 365, label: "1 year" },
];

const dayMs = 24 * 3600 * 1000;

const CompareLine = ({ label, cur, prev }) => {
  let body;
  if (prev === 0 && cur === 0) body = <span className="text-slate-400">No change</span>;
  else if (prev === 0) body = <span className="font-semibold text-emerald-600">New this period</span>;
  else {
    const pct = ((cur - prev) / prev) * 100;
    if (Math.abs(pct) < 0.05) body = <span className="text-slate-400">No change</span>;
    else body = (
      <span className={`inline-flex items-center gap-1 font-semibold ${pct > 0 ? "text-emerald-600" : "text-red-500"}`}>
        {pct > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
        {Math.abs(pct).toFixed(0)}% {pct > 0 ? "up" : "down"}
      </span>
    );
  }
  return (
    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="flex items-center gap-2">
        <span className={`${MONO} text-slate-700`}>{money(cur)} <span className="text-slate-400">vs</span> {money(prev)}</span>
        {body}
      </span>
    </div>
  );
};

/**
 * Home > Analytics — job value + active estimating time over a selectable
 * period, month/year comparisons, and the per-job time leaderboard.
 * Prices are shown here (never on the Projects list).
 */
export default function AnalyticsView() {
  const { workspace, timeDaily } = useTakeoff();
  const [days, setDays] = useState(30);
  const projects = workspace?.projects || [];

  const stats = useMemo(() => {
    const now = Date.now();
    const since = now - days * dayMs;
    const sinceDate = new Date(since).toISOString().slice(0, 10);
    const included = projects.filter((p) => (p.created_at || 0) >= since);
    const estValue = included.reduce((s, p) => s + (p.estimate_total || 0), 0);
    const activeSecs = timeDaily
      .filter((t) => t.work_date >= sinceDate)
      .reduce((s, t) => s + (t.total_seconds || 0), 0);
    // calendar month / year comparisons by job creation value
    const d = new Date();
    const mStart = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const lmStart = new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime();
    const yStart = new Date(d.getFullYear(), 0, 1).getTime();
    const lyStart = new Date(d.getFullYear() - 1, 0, 1).getTime();
    const inRange = (p, a, b) => (p.created_at || 0) >= a && (p.created_at || 0) < b;
    const val = (list) => list.reduce((s, p) => s + (p.estimate_total || 0), 0);
    return {
      estValue,
      jobsAdded: included.length,
      activeSecs,
      avg: included.length ? estValue / included.length : 0,
      monthCur: val(projects.filter((p) => inRange(p, mStart, Infinity))),
      monthPrev: val(projects.filter((p) => inRange(p, lmStart, mStart))),
      yearCur: val(projects.filter((p) => inRange(p, yStart, Infinity))),
      yearPrev: val(projects.filter((p) => inRange(p, lyStart, yStart))),
    };
  }, [projects, timeDaily, days]);

  const timeByJob = useMemo(() =>
    [...projects]
      .filter((p) => (p.total_seconds || 0) > 0)
      .sort((a, b) =>
        (b.total_seconds || 0) - (a.total_seconds || 0) ||
        (b.created_at || 0) - (a.created_at || 0) ||
        a.name.localeCompare(b.name)),
    [projects]);
  const maxSecs = Math.max(1, ...timeByJob.map((p) => p.total_seconds || 0));

  const cards = [
    { label: "Estimated value", value: money(stats.estValue), testid: "analytics-estimated-value" },
    { label: "Jobs added", value: String(stats.jobsAdded), testid: "analytics-jobs-added" },
    { label: "Active time", value: fmtDuration(stats.activeSecs), testid: "analytics-active-time" },
    { label: "Average job value", value: money(stats.avg), testid: "analytics-avg-value" },
  ];

  return (
    <div className="space-y-4" data-testid="takeoff-analytics">
      <div className="flex flex-wrap items-center gap-1.5">
        {RANGES.map((r) => (
          <button key={r.days} onClick={() => setDays(r.days)} data-testid={`analytics-range-${r.days}`}
            aria-pressed={days === r.days}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${days === r.days ? "bg-[#f97316] text-white" : "border border-slate-300 bg-white text-slate-500 hover:bg-slate-50"}`}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className={`${CARD} p-4`}>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{c.label}</p>
            <p className={`${MONO} mt-1 text-xl font-bold text-slate-900`} data-testid={c.testid}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className={CARD}>
        <CompareLine label="This month vs last month (job value)" cur={stats.monthCur} prev={stats.monthPrev} />
        <CompareLine label="This year vs last year (job value)" cur={stats.yearCur} prev={stats.yearPrev} />
      </div>

      <div className={CARD} data-testid="analytics-time-by-job">
        <p className="border-b border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Active time by job</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                <th className="px-4 py-2">Project</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Active time</th>
                <th className="px-3 py-2 text-right">Current estimate</th>
                <th className="px-4 py-2 w-48"></th>
              </tr>
            </thead>
            <tbody>
              {timeByJob.map((p) => (
                <tr key={p.project_id} className="border-b border-slate-50 last:border-0">
                  <td className="px-4 py-2 font-medium text-slate-800">{p.name}</td>
                  <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                  <td className={`px-3 py-2 ${MONO} text-slate-700`}>{fmtDuration(p.total_seconds)}</td>
                  <td className={`px-3 py-2 text-right ${MONO} font-semibold text-slate-900`}>{money(p.estimate_total || 0)}</td>
                  <td className="px-4 py-2">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-[#f97316]" style={{ width: `${Math.max(2, ((p.total_seconds || 0) / maxSecs) * 100)}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
              {!timeByJob.length && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">No active estimating time recorded yet — time accrues while you work in Plans &amp; Takeoffs or Estimate.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
